// Presentational community browse/detail (D-43). SSR-safe: plain <a href>, no
// AuthContext, no react-router. CommunityPage (SPA) and CommunityPrerender share these.

import { COMMUNITY_CATEGORIES, type CommunityCategory } from "@checklist/shared";
import { BadgeCheck } from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useState,
  type ReactNode,
} from "react";
import { EmptyState } from "../components/ui/EmptyState.js";
import { PageHeader } from "../components/ui/PageHeader.js";
import { SearchInput } from "../components/ui/SearchInput.js";
import { SkeletonCardGrid } from "../components/ui/SkeletonCard.js";
import { CARD_GRID_CLS } from "../components/ui/Card.js";
import { AnchorButton, Button } from "../components/ui/Button.js";
import { categoryHref } from "../lib/communityCategory.js";
import { useSignupMode } from "../hooks/useSignupMode.js";
import { signupLabel } from "../lib/signupCopy.js";

export type CommunityCard = {
  official?: boolean;
  id: string;
  name: string;
  description?: string;
  itemCount: number;
  ownerScreenName: string;
  copyCount: number;
  aiGenerated?: boolean;
};

export type CommunityTemplateCard = {
  official?: boolean;
  id: string;
  name: string;
  description?: string;
  sectionNames: string[];
  ownerScreenName: string;
  copyCount: number;
};

export type CommunityDetailItem = {
  id: string;
  text: string;
};

export type CommunityDetailGroup = {
  id: string;
  name: string;
  items: CommunityDetailItem[];
};

export type CommunityDetail = {
  official?: boolean;
  id: string;
  name: string;
  kind: string;
  description?: string;
  items: CommunityDetailItem[];
  subgroups: CommunityDetailGroup[];
  ownerScreenName: string;
  copyCount: number;
};

export type CommunityTemplateDetailSection =
  | {
      type: "list";
      listId: string;
      name: string;
      items: CommunityDetailItem[];
      subgroups: CommunityDetailGroup[];
    }
  | {
      type: "inline";
      name: string;
      items: CommunityDetailItem[];
    };

export type CommunityTemplateDetail = {
  official?: boolean;
  id: string;
  name: string;
  description?: string;
  ownerScreenName: string;
  copyCount: number;
  sections: CommunityTemplateDetailSection[];
};

export function OwnerByline({ ownerScreenName, official }: { ownerScreenName: string; official?: boolean }) {
  return (
    <>
      by {ownerScreenName}
      {official ? (
        <span className="ml-1 inline-flex items-center gap-0.5 align-middle font-medium text-brand-700" title="Official NeatTick content">
          <BadgeCheck size={14} aria-hidden="true" />
          Official
        </span>
      ) : null}
    </>
  );
}

function usedLabel(count: number): string {
  return `Used ${count} time${count === 1 ? "" : "s"}`;
}

function chipClass(active: boolean): string {
  return [
    "inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border px-3 text-sm font-medium no-underline outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600",
    active
      ? "border-brand-600 bg-brand-600 text-white"
      : "border-line bg-surface text-ink hover:border-brand-200 hover:bg-brand-50/40",
  ].join(" ");
}

function CategoryChips({
  category,
  query,
}: {
  category: CommunityCategory | null;
  query: string;
}) {
  return (
    <nav aria-label="Categories" className="mt-3 flex flex-wrap gap-2">
      <a
        href={categoryHref(null, query)}
        aria-current={category ? undefined : "page"}
        className={chipClass(!category)}
      >
        All
      </a>
      {COMMUNITY_CATEGORIES.map((entry) => (
        <a
          key={entry.slug}
          href={categoryHref(entry.slug, query)}
          aria-current={category === entry.slug ? "page" : undefined}
          className={chipClass(category === entry.slug)}
        >
          {entry.label}
        </a>
      ))}
    </nav>
  );
}

export function CommunityIndexView({
  lists,
  templates = [],
  category = null,
  query = "",
  searchValue = "",
  onSearchChange,
  onDebouncedSearch,
  categoryNotice,
  loading = false,
  loadingMore = false,
  cursor,
  onLoadMore,
  emptyPrimary,
  emptyBody,
  banner,
}: {
  lists: CommunityCard[];
  templates?: CommunityTemplateCard[];
  category?: CommunityCategory | null;
  query?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onDebouncedSearch?: (value: string) => void;
  categoryNotice?: string | null;
  loading?: boolean;
  loadingMore?: boolean;
  cursor?: string;
  onLoadMore?: () => void;
  emptyPrimary?: ReactNode;
  emptyBody?: string;
  banner?: ReactNode;
}) {
  const { mode: signupMode } = useSignupMode();
  const showZeroResults = !loading && lists.length === 0 && templates.length === 0;
  const searchChange = onSearchChange ?? (() => {});
  const filtered = Boolean(query || category);
  const chipQuery = (searchValue || query).trim();

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:py-10">
      <PageHeader
        title="Community"
        subtitle="Ready-made lists and templates from NeatTick and the community. Start a checklist in one click, or copy one to make it yours."
      />
      {banner}

      <div className="mt-4">
        <SearchInput
          id="communitySearch"
          aria-label="Search lists and templates"
          placeholder="Search lists and templates..."
          value={searchValue}
          onChange={searchChange}
          onDebouncedChange={onDebouncedSearch}
          maxLength={100}
        />
        <CategoryChips category={category} query={chipQuery} />
      </div>

      {categoryNotice ? (
        <p className="mt-4 text-sm text-muted">{categoryNotice}</p>
      ) : null}

      <div className={loading ? "min-h-[60vh]" : undefined}>
      {loading ? (
        // First page is 25 cards (api/src/lib/community.ts PAGE_SIZE).
        <SkeletonCardGrid count={25} />
      ) : showZeroResults ? (
        <EmptyState
          title={
            filtered
              ? query
                ? `No lists or templates match "${query}"`
                : "No lists or templates in this category"
              : "No published lists yet"
          }
          body={
            emptyBody ??
            (filtered
              ? "Try a different search, or generate a list with AI."
              : signupLabel("publish", signupMode))
          }
          primary={
            emptyPrimary ??
            (filtered ? (
              <AnchorButton
                href={`/register?next=${encodeURIComponent(categoryHref(category, query))}`}
              >
                {signupLabel("aiCreate", signupMode)}
              </AnchorButton>
            ) : (
              <AnchorButton href="/register">{signupLabel("nav", signupMode)}</AnchorButton>
            ))
          }
        />
      ) : (
        <>
          {templates.length > 0 ? (
            <section className="mt-8">
              <h2 className="text-lg font-semibold text-ink">Templates</h2>
              <ul className={`mt-4 ${CARD_GRID_CLS}`}>
                {templates.map((template) => (
                  <li key={template.id}>
                    <a
                      href={`/community/templates/${template.id}`}
                      className="flex h-full flex-col rounded-2xl border border-line bg-surface p-5 no-underline shadow-sm transition hover:border-brand-200 hover:bg-brand-50/40"
                    >
                      <h3 className="text-base font-medium text-ink">{template.name}</h3>
                      {template.description ? (
                        <p className="mt-1 line-clamp-2 text-sm text-muted">{template.description}</p>
                      ) : null}
                      {template.sectionNames.length > 0 ? (
                        <p className="mt-3 text-xs text-muted-sm">
                          {template.sectionNames.join(" + ")}
                        </p>
                      ) : null}
                      <p className="mt-1 text-xs tabular-nums text-muted-sm">
                        <OwnerByline ownerScreenName={template.ownerScreenName} official={template.official} />
                        {" · "}
                        {usedLabel(template.copyCount)}
                      </p>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {lists.length > 0 ? (
            <section className={templates.length > 0 ? "mt-10" : "mt-6"}>
              {templates.length > 0 ? (
                <h2 className="text-lg font-semibold text-ink">Lists</h2>
              ) : null}
              <ul
                className={
                  templates.length > 0
                    ? `mt-4 ${CARD_GRID_CLS}`
                    : CARD_GRID_CLS
                }
              >
                {lists.map((list) => {
                  const TitleTag = templates.length > 0 ? "h3" : "h2";
                  return (
                    <li key={list.id}>
                    <a
                      href={`/community/${list.id}`}
                      className="flex h-full flex-col rounded-2xl border border-line bg-surface p-5 no-underline shadow-sm transition hover:border-brand-200 hover:bg-brand-50/40"
                    >
                      <TitleTag className="text-base font-medium text-ink">{list.name}</TitleTag>
                      {list.description ? (
                        <p className="mt-1 line-clamp-2 text-sm text-muted">{list.description}</p>
                      ) : null}
                      {list.aiGenerated ? (
                        <span className="mt-2 w-fit rounded-full bg-slate-100 px-2 py-0.5 text-xs text-muted">
                          AI-generated
                        </span>
                      ) : null}
                      <p className="mt-3 text-xs tabular-nums text-muted-sm">
                        {list.itemCount} item{list.itemCount === 1 ? "" : "s"}
                        {" · "}
                        <OwnerByline ownerScreenName={list.ownerScreenName} official={list.official} />
                        {" · "}
                        {usedLabel(list.copyCount)}
                      </p>
                    </a>
                  </li>
                  );
                })}
              </ul>
              {cursor && onLoadMore ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="mx-auto mt-6 block"
                  disabled={loadingMore}
                  onClick={onLoadMore}
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </Button>
              ) : null}
            </section>
          ) : null}
        </>
      )}
      </div>
    </div>
  );
}

type PreviewKind = "list" | "template";

/** Side-by-side action buttons. Overrides Button's fixed height so a wrapped label stays fully visible. */
export const communityPairButtonClass =
  "h-auto! min-h-9 w-full min-w-0 whitespace-normal px-2.5! py-2 text-center leading-snug sm:px-3!";

function previewCopy(kind: PreviewKind): { tutorial: string; checkbox: string } {
  const noun = kind === "list" ? "list" : "template";
  return {
    tutorial: `This is a community ${noun}. Start a checklist to check items off or edit them.`,
    checkbox: `This is a community ${noun}. Start a checklist to check items off or edit them.`,
  };
}

const PreviewTipContext = createContext<{
  openId: string | null;
  setOpenId: (id: string | null) => void;
} | null>(null);

function PreviewTipProvider({ children }: { children: ReactNode }) {
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    if (!openId) return;
    function onPointer(e: PointerEvent) {
      const el = e.target instanceof Element ? e.target : null;
      if (el?.closest("[data-preview-checkbox], [data-preview-tip]")) return;
      setOpenId(null);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpenId(null);
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [openId]);

  return <PreviewTipContext.Provider value={{ openId, setOpenId }}>{children}</PreviewTipContext.Provider>;
}

function PreviewCheckbox({ kind }: { kind: PreviewKind }) {
  const ctx = useContext(PreviewTipContext);
  const id = useId();
  const open = ctx?.openId === id;
  const tipId = useId();

  return (
    <span className="relative mt-0.5 inline-flex shrink-0">
      <button
        type="button"
        data-preview-checkbox=""
        className="relative inline-flex size-5 items-center justify-center rounded-md text-slate-400 before:absolute before:-inset-1.5 before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
        aria-expanded={open}
        aria-controls={tipId}
        aria-label="Preview checkbox"
        onClick={() => ctx?.setOpenId(open ? null : id)}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
          <rect
            x="1.25"
            y="1.25"
            width="17.5"
            height="17.5"
            rx="5"
            fill="white"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      </button>
      {open ? (
        <span
          id={tipId}
          role="status"
          data-preview-tip=""
          className="absolute left-0 top-[calc(100%+8px)] z-30 w-72 max-w-[calc(100vw-2.5rem)] rounded-xl border border-brand-200 bg-white px-3 py-2.5 text-left text-sm leading-6 text-ink shadow-lg"
        >
          <span
            aria-hidden="true"
            className="absolute -top-1.5 left-2.5 h-3 w-3 rotate-45 border-l border-t border-brand-200 bg-white"
          />
          <span className="relative">{previewCopy(kind).checkbox}</span>
        </span>
      ) : null}
    </span>
  );
}

function CommunityItemRows({
  items,
  kind,
  className,
}: {
  items: CommunityDetailItem[];
  kind: PreviewKind;
  className: string;
}) {
  if (items.length === 0) return null;
  return (
    <ul className={className}>
      {items.map((item) => (
        <li
          key={item.id}
          className="flex items-start gap-2.5 border-b border-line/60 py-1.5 last:border-0"
        >
          <PreviewCheckbox kind={kind} />
          <span className="min-w-0 flex-1">{item.text}</span>
        </li>
      ))}
    </ul>
  );
}

function CommunityPreviewHeader({
  kind,
  name,
  ownerScreenName,
  official,
  copyCount,
  description,
  banner,
  actions,
}: {
  kind: PreviewKind;
  name: string;
  ownerScreenName: string;
  official?: boolean;
  copyCount: number;
  description?: string;
  banner?: ReactNode;
  actions: ReactNode;
}) {
  return (
    <div className="sticky top-[var(--shell-header,3.5rem)] z-20 -mx-4 border-b border-line bg-slate-50 px-4 pb-4 pt-6 shadow-[0_10px_16px_-14px_rgba(15,23,42,0.45)] sm:pt-8">
      {banner ? <div className="mb-4">{banner}</div> : null}
      <p className="mb-4">
        <a href="/community" className="text-sm font-medium text-brand-700 no-underline hover:underline">
          ← Community
        </a>
      </p>
      <h1 className="text-2xl font-semibold text-ink sm:text-3xl">{name}</h1>
      <p className="mt-2 text-sm text-muted">
        <OwnerByline ownerScreenName={ownerScreenName} official={official} /> · <span className="tabular-nums">{usedLabel(copyCount)}</span>
      </p>
      {description ? (
        <p className="mt-3 line-clamp-3 text-base leading-7 text-muted">{description}</p>
      ) : null}
      <div className="relative mt-4">
        <p className="rounded-xl border border-brand-200 bg-brand-50 px-3 py-2.5 text-sm leading-6 text-ink">
          {previewCopy(kind).tutorial}
        </p>
        <span
          aria-hidden="true"
          className="absolute left-[25%] top-full h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-r border-brand-200 bg-brand-50"
        />
      </div>
      <div className="mt-3">{actions}</div>
    </div>
  );
}

export function CommunityDetailView({
  list,
  cta,
  footer,
  banner,
}: {
  list: CommunityDetail;
  cta?: ReactNode;
  footer?: ReactNode;
  banner?: ReactNode;
}) {
  const { mode: signupMode } = useSignupMode();
  const signupHref = `/register?next=${encodeURIComponent(`/community/${list.id}`)}`;
  const actions = cta ?? (
    <AnchorButton href={signupHref} fullWidth>
      {signupLabel("start", signupMode)}
    </AnchorButton>
  );

  return (
    <PreviewTipProvider>
      <article className="mx-auto w-full max-w-2xl px-4 pb-10">
        <CommunityPreviewHeader
          kind="list"
          name={list.name}
          ownerScreenName={list.ownerScreenName}
          official={list.official}
          copyCount={list.copyCount}
          description={list.description}
          banner={banner}
          actions={actions}
        />

        <CommunityItemRows
          items={list.items}
          kind="list"
          className="mt-6 space-y-1 text-sm text-ink"
        />

        {list.subgroups.map((group) => (
          <section key={group.id} className="mt-8">
            <h2 className="text-base font-semibold text-ink">{group.name}</h2>
            <CommunityItemRows
              items={group.items}
              kind="list"
              className="mt-2 space-y-1 text-sm text-ink"
            />
          </section>
        ))}

        <div className="mt-8">{actions}</div>
        {footer}
      </article>
    </PreviewTipProvider>
  );
}

export function CommunityTemplateDetailView({
  template,
  cta,
  footer,
  banner,
}: {
  template: CommunityTemplateDetail;
  cta?: ReactNode;
  footer?: ReactNode;
  banner?: ReactNode;
}) {
  const { mode: signupMode } = useSignupMode();
  const signupHref = `/register?next=${encodeURIComponent(
    `/community/templates/${template.id}`
  )}`;
  const actions = cta ?? (
    <AnchorButton href={signupHref} fullWidth>
      {signupLabel("start", signupMode)}
    </AnchorButton>
  );

  return (
    <PreviewTipProvider>
      <article className="mx-auto w-full max-w-2xl px-4 pb-10">
        <CommunityPreviewHeader
          kind="template"
          name={template.name}
          ownerScreenName={template.ownerScreenName}
          official={template.official}
          copyCount={template.copyCount}
          description={template.description}
          banner={banner}
          actions={actions}
        />

        {template.sections.map((section, index) => (
          <section key={section.type === "list" ? section.listId : `inline-${index}`} className="mt-8">
            <h2 className="text-base font-semibold text-ink">{section.name}</h2>
            <CommunityItemRows
              items={section.items}
              kind="template"
              className="mt-2 space-y-1 text-sm text-ink"
            />
            {section.type === "list"
              ? section.subgroups.map((group) => (
                  <div key={group.id} className="mt-4">
                    <h3 className="text-sm font-semibold text-ink">{group.name}</h3>
                    <CommunityItemRows
                      items={group.items}
                      kind="template"
                      className="mt-2 space-y-1 text-sm text-ink"
                    />
                  </div>
                ))
              : null}
          </section>
        ))}

        {footer}
      </article>
    </PreviewTipProvider>
  );
}
